import { Dropdown, Toolbar } from '@bynder/design-system';
import {
  IconAlignBottom,
  IconAlignRight,
  IconBackgroundFill,
  IconDelete,
  IconMerge,
  IconSplit,
} from '@bynder/icons';
import type { Editor } from '@tiptap/core';
import { selectedRect } from '@tiptap/pm/tables';
import { useEffect, useRef, useState } from 'react';
import type { EditorFormats } from '../../data/.types/editorFormats';
import { getValueOfCssVariable } from '../../helpers/dom.ts';
import { getTableCellsFromSelection } from '../../helpers/tiptap';
import { CellColorPickerDropdown } from '../color-picker/color-picker-table-cell.tsx';

interface TableOptionsDropdownProps {
  editor: Editor;
  editorFormats?: EditorFormats;
}

export default function TableOptionsDropdown({
  editor,
  editorFormats,
}: TableOptionsDropdownProps) {
  const toolbarWrapper = useRef<HTMLDivElement>(null);

  const [tableDimensions, setTableDimensions] = useState({
    columnCount: getColumnCount(editor),
    rowCount: getRowCount(editor),
  });

  useEffect(() => {
    const onSelectionUpdate = ({ editor }: { editor: Editor }) => {
      updateTableToolbarPosition(toolbarWrapper.current, editor);

      setTableDimensions({
        columnCount: getColumnCount(editor),
        rowCount: getRowCount(editor),
      });
    };

    editor.on('selectionUpdate', onSelectionUpdate);
    editor.on('focus', onSelectionUpdate);

    return () => {
      editor.off('selectionUpdate', onSelectionUpdate);
      editor.off('focus', onSelectionUpdate);
    };
  }, [editor.on, editor.off]);

  useEffect(() => {
    launchToolbarOnKeyboardShortcutEffect(toolbarWrapper.current, editor);
  });

  function multipleRowsSelected(editor: Editor) {
    if (!editor.isActive('table')) return false;

    const tableRect = selectedRect(editor.state);
    if (!tableRect) return false;

    const { top, bottom } = tableRect;
    const selectedRowCount = bottom - top;

    return selectedRowCount > 1;
  }

  function multipleColumnsSelected(editor: Editor) {
    if (!editor.isActive('table')) return false;

    const tableRect = selectedRect(editor.state);
    if (!tableRect) return false;

    const { right, left } = tableRect;
    const selectedColumnCount = right - left;

    return selectedColumnCount > 1;
  }

  return (
    <div className="tea-table-toolbar" tabIndex={-1} ref={toolbarWrapper}>
      <Toolbar direction="vertical">
        <Dropdown
          trigger={({ isOpen = true, ...triggerProps }) => (
            <Toolbar.Button
              aria-label="See table options"
              isPressed={isOpen}
              rightIcon={<Dropdown.Arrow />}
              {...triggerProps}
            />
          )}
        >
          <Dropdown.Item
            submenu={
              <CellColorPickerDropdown
                editor={editor}
                colorOptions={editorFormats?.colors}
                onChange={(color) => {
                  editor
                    .chain()
                    .focus()
                    .setCellAttribute(
                      'backgroundColor',
                      getValueOfCssVariable(color.value) ?? color.value,
                    )
                    .run();
                }}
              />
            }
          >
            <IconBackgroundFill />
            Background colour
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => editor.commands.mergeCells()}
            isDisabled={getTableCellsFromSelection(editor).length <= 1}
          >
            <IconMerge />
            Merge cells
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => editor.commands.splitCell()}
            isDisabled={hasMergedCellAttributes(editor) === false}
          >
            <IconSplit />
            Split cells
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              editor.commands.addColumnAfter();
              updateTableToolbarPosition(toolbarWrapper.current, editor);
            }}
          >
            <IconAlignRight />
            Add column right
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => {
              editor.commands.addRowAfter();
              updateTableToolbarPosition(toolbarWrapper.current, editor);
            }}
          >
            <IconAlignBottom />
            Add row below
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={() => editor.commands.deleteColumn()}
            isDisabled={
              tableDimensions.columnCount <= 1 ||
              multipleColumnsSelected(editor)
            }
          >
            <IconDelete />
            Delete column
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => editor.commands.deleteRow()}
            isDisabled={
              tableDimensions.rowCount <= 1 || multipleRowsSelected(editor)
            }
          >
            <IconDelete />
            Delete row
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => editor.commands.deleteTable()}>
            <IconDelete />
            Delete table
          </Dropdown.Item>
        </Dropdown>
      </Toolbar>
    </div>
  );
}

function updateTableToolbarPosition(
  toolbar: HTMLDivElement | null,
  editor: Editor,
) {
  const tdInFocus = document.querySelector('td.has-focus');
  const toolbarButtonWidth = 40;
  const marginPx = 6;

  if (toolbar === null) return;

  if (tdInFocus === null || editor.isFocused === false) {
    toolbar.style.display = 'none';
    toolbar.setAttribute('tabIndex', '-1');
    return;
  }

  // Get the editor's position relative to the viewport
  const editorRect = editor.view.dom.getBoundingClientRect();

  // Get the cell's position relative to the viewport
  const cellRect = tdInFocus.getBoundingClientRect();

  // Subtract editor top position from the cell top position and add
  // the desired margin to get the relative top position of the toolbar
  const topPosition = cellRect.top - editorRect.top + marginPx;

  // Use the right edge of the cell to align the toolbar
  //
  // Subtract editor left position from the cell right position,
  // then subtract the width of the button and the desired margin to
  // to get the relative left position of the toolbar
  const leftPosition =
    cellRect.right - editorRect.left - toolbarButtonWidth - marginPx;

  toolbar.style.display = 'block';
  toolbar.style.top = `${topPosition}px`;
  toolbar.style.left = `${leftPosition}px`;
  toolbar.setAttribute('tabIndex', '0');
}

function launchToolbarOnKeyboardShortcutEffect(
  toolbarWrapper: HTMLDivElement | null,
  editor: Editor,
) {
  const onKeyDown = (event: KeyboardEvent) => {
    if (!toolbarWrapper || !editor.isActive('table')) return;

    const shortcutWasMatched = event.key === 'F10' && event.shiftKey;

    const toolbarButton: HTMLButtonElement | null =
      toolbarWrapper.querySelector('[aria-label="See table options"]');

    // It's a bit of a hack, but I can't see a nice way in the DS to manually
    // open and close the dropdown other than clicking the trigger
    if (shortcutWasMatched && toolbarButton !== null) {
      toolbarButton.click();

      // set focus on the first button from the menu
      const firstButton = toolbarWrapper.querySelector('button');
      if (firstButton) {
        firstButton.focus();
      }
    }
  };

  document.addEventListener('keydown', onKeyDown);

  // remove listener on unmount
  return () => {
    document.removeEventListener('keydown', onKeyDown);
  };
}

function getColumnCount(editor: Editor) {
  const activeTableRow = editor.view.dom.querySelector('tr.has-focus');
  if (activeTableRow === null) return 0;

  return activeTableRow.querySelectorAll('td').length;
}

function getRowCount(editor: Editor) {
  const activeTable = editor.view.dom.querySelector('table.has-focus');
  if (activeTable === null) return 0;

  return activeTable.querySelectorAll('tr').length;
}

function hasMergedCellAttributes(editor: Editor) {
  const hasColspan = editor.getAttributes('tableCell').colspan > 1;
  const hasRowSpan = editor.getAttributes('tableCell').rowspan > 1;
  return hasColspan || hasRowSpan;
}
